import React, { useEffect, useState } from "react";
import styles from "./LedgerStatement.module.css";
import Paginations from "../Customs/pagination/Paginations";
import { fetchClientLedger, fetchVendorLedger } from "../Services/ledger";
import { LedgerEntry, LedgerLink, LedgerPartyType, LedgerStatement as LedgerStatementData } from "../Models/Models";
import TransactionDetailModal, { TransactionDetailTarget } from "./TransactionDetailModal";

const PAGE_SIZE = 20;

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All types" },
  { value: "SALE", label: "Sale" },
  { value: "PURCHASE", label: "Purchase" },
  { value: "PAYMENT", label: "Payment" },
  { value: "REFUND", label: "Refund" },
  { value: "REVERSAL", label: "Reversal" },
  { value: "ADJUSTMENT", label: "Adjustment" },
];

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "₹0.00";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value: unknown): string {
  if (value == null) return "--";
  const raw = String(value).trim();
  if (!raw) return "--";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? "--" : d.toLocaleDateString();
}

function canOpenLink(link?: LedgerLink | null): boolean {
  if (!link) return false;
  const kind = String(link.kind || "").toUpperCase();
  if (kind === "PURCHASE" || kind === "VENDOR_PAYMENT") return false;
  return Boolean(link.invoice_id || link.sale_order_id || link.receipt_id || link.payment_id);
}

interface LedgerStatementProps {
  partyType: LedgerPartyType;
  partyId: number;
  refreshKey?: number;
  customerName?: string;
  onLoaded?: (statement: LedgerStatementData | null) => void;
}

const LedgerStatement: React.FC<LedgerStatementProps> = ({
  partyType,
  partyId,
  refreshKey = 0,
  customerName,
  onLoaded,
}) => {
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState<LedgerStatementData | null>(null);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [detail, setDetail] = useState<TransactionDetailTarget | null>(null);

  useEffect(() => {
    setPage(1);
  }, [partyId, partyType, fromDate, toDate, transactionType]);

  useEffect(() => {
    if (!partyId) return;
    setLoading(true);
    const fetcher = partyType === "VENDOR" ? fetchVendorLedger : fetchClientLedger;
    fetcher(partyId, {
      page,
      limit: PAGE_SIZE,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      transactionTypes: transactionType || undefined,
    })
      .then((res: any) => {
        const data = res?.data || null;
        setStatement(data);
        onLoaded?.(data);
      })
      .catch(() => {
        setStatement(null);
        onLoaded?.(null);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyType, partyId, page, fromDate, toDate, transactionType, refreshKey]);

  const entries: LedgerEntry[] = statement?.entries || [];
  const totals = statement?.totals;
  const totalPages = statement?.pagination?.total_pages || 1;
  const outstandingLabel =
    partyType === "VENDOR" ? "Payable" : "Receivable";
  const invoiceHeader = partyType === "VENDOR" ? "Bill / PO" : "Invoice";
  const partyLabel = statement?.company_name || statement?.party_name || customerName;

  const openLink = (side: "invoice" | "payment", link?: LedgerLink | null) => {
    if (!canOpenLink(link) || !link) return;
    setDetail({ side, link, customerName: partyLabel });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.summaryRow}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Opening</div>
          <div className={styles.summaryValue}>
            {formatMoney(statement?.opening_balance)}
            <span className={styles.balanceType}>
              {statement?.opening_balance_type || "SETTLED"}
            </span>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Total Debit</div>
          <div className={`${styles.summaryValue} ${styles.debit}`}>
            {formatMoney(totals?.total_debit)}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Total Credit</div>
          <div className={`${styles.summaryValue} ${styles.credit}`}>
            {formatMoney(totals?.total_credit)}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>{outstandingLabel}</div>
          <div className={`${styles.summaryValue} ${styles.outstanding}`}>
            {formatMoney(totals?.outstanding ?? statement?.closing_balance)}
            <span className={styles.balanceType}>
              {totals?.outstanding_type || statement?.closing_balance_type || "SETTLED"}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <input
          type="date"
          className={styles.filterInput}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          max={toDate || undefined}
        />
        <input
          type="date"
          className={styles.filterInput}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate || undefined}
        />
        <select
          className={styles.filterInput}
          value={transactionType}
          onChange={(e) => setTransactionType(e.target.value)}
        >
          {TYPE_OPTIONS.filter((opt) => {
            if (partyType === "CLIENT" && opt.value === "PURCHASE") return false;
            if (partyType === "VENDOR" && opt.value === "SALE") return false;
            return true;
          }).map((opt) => (
            <option key={opt.value || "all"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.empty}>Loading ledger...</div>
        ) : entries.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>No ledger entries</div>
            <div className={styles.emptyNote}>
              Entries appear here as sales, purchases, payments, refunds, and adjustments are recorded.
            </div>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>{invoiceHeader}</th>
                <th>Payment</th>
                <th>Debit</th>
                <th>Credit</th>
                <th>Outstanding Balance</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => (
                <tr key={row.entry_id}>
                  <td>{formatDate(row.entry_date)}</td>
                  <td>
                    {row.invoice ? (
                      <button
                        type="button"
                        className={canOpenLink(row.invoice) ? styles.docLink : styles.docPlain}
                        onClick={() => openLink("invoice", row.invoice)}
                        disabled={!canOpenLink(row.invoice)}
                      >
                        <span className={styles.docNumber}>{row.invoice.number}</span>
                        <span className={styles.docAmount}>{formatMoney(row.invoice.amount)}</span>
                        {row.related_type_label ? (
                          <span className={styles.docCaption}>{row.related_type_label}</span>
                        ) : null}
                      </button>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td>
                    {row.payment ? (
                      <button
                        type="button"
                        className={canOpenLink(row.payment) ? styles.docLink : styles.docPlain}
                        onClick={() => openLink("payment", row.payment)}
                        disabled={!canOpenLink(row.payment)}
                      >
                        <span className={styles.docNumber}>{row.payment.number}</span>
                        <span className={styles.docAmount}>{formatMoney(row.payment.amount)}</span>
                        {row.related_type_label ? (
                          <span className={styles.docCaption}>{row.related_type_label}</span>
                        ) : null}
                      </button>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td className={styles.debit}>
                    {Number(row.debit) > 0 ? formatMoney(row.debit) : "—"}
                  </td>
                  <td className={styles.credit}>
                    {Number(row.credit) > 0 ? formatMoney(row.credit) : "—"}
                  </td>
                  <td>
                    {formatMoney(row.running_balance ?? row.outstanding)}
                    <span className={styles.balanceType}>
                      {row.running_balance_type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && entries.length > 0 && (
        <div className={styles.paginationRow}>
          <Paginations
            totalItems={totalPages * PAGE_SIZE}
            itemsCountPerPage={PAGE_SIZE}
            changePage={setPage}
            changeCurrentPage={page}
          />
        </div>
      )}

      <TransactionDetailModal
        open={detail != null}
        target={detail}
        onClose={() => setDetail(null)}
      />
    </div>
  );
};

export default LedgerStatement;
